import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { Navigate, useLocation, useNavigate, useParams } from "react-router-dom";
import type { DemoBundle } from "../../shared/types";
import { api } from "../api";
import { setMonitoringTags } from "../monitoring";
import { useShell } from "./AdminShell";
import { hasPendingSave, setUnsaved } from "./dirty";
import Studio from "./studio/Studio";
import AnalyticsTab from "./tabs/AnalyticsTab";
import BasicsTab from "./tabs/BasicsTab";
import PathsTab from "./tabs/PathsTab";
import PreviewTab from "./tabs/PreviewTab";
import PublishTab from "./tabs/PublishTab";
import SurveyTab from "./tabs/SurveyTab";

/** Every screen of the demo editor. The top bar links to most of these; Publish sits behind its action button. */
export const TABS = [
  { key: "basics", label: "Settings / Basics" },
  { key: "outcomes", label: "Paths" },
  { key: "studio", label: "Studio" },
  { key: "survey", label: "Survey" },
  { key: "preview", label: "Preview" },
  { key: "analytics", label: "Analytics" },
  { key: "publish", label: "Publish" },
] as const;
export type TabKey = (typeof TABS)[number]["key"];

const HEADINGS: Record<string, { title: string; sub: string }> = {
  basics: { title: "Settings / Basics", sub: "Name and link, the end-of-demo button, screen transitions and lead capture." },
  survey: { title: "Survey", sub: "Optional intake questions. The answers route each viewer to the version of the tour that fits them." },
  outcomes: { title: "Paths", sub: "Which path each viewer sees: every path's routing tags, and which wins a tie." },
  preview: { title: "Preview", sub: "Play the demo as a viewer would, or test specific tag routings. Previews aren't counted in analytics." },
  publish: { title: "Publish & share", sub: "Check the demo, publish it, and copy a link or embed code." },
  analytics: { title: "Analytics", sub: "Views, completion, where viewers drop off, survey answers and leads." },
};

interface EditorCtx {
  bundle: DemoBundle;
  setBundle: (b: DemoBundle) => void;
  setDirty: (d: boolean) => void;
  go: (tab: TabKey, query?: string) => void;
}
const Ctx = createContext<EditorCtx | null>(null);
export const useEditor = () => useContext(Ctx)!;

export default function DemoEditor() {
  const { demoId = "", tab = "basics" } = useParams();
  const { refreshNav } = useShell();
  const nav = useNavigate();
  const { search } = useLocation();
  const [bundle, setBundleState] = useState<DemoBundle | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const dirty = useRef(false);

  useEffect(() => {
    setBundleState(null);
    api.demo(demoId).then(setBundleState).catch((e) => setLoadError(e.message));
  }, [demoId]);

  // Error reports from here say which demo and tab they came from.
  useEffect(() => {
    setMonitoringTags({ demo_id: demoId, tab });
    return () => setMonitoringTags({ demo_id: null, tab: null });
  }, [demoId, tab]);

  useEffect(() => {
    const warn = (e: BeforeUnloadEvent) => { if (dirty.current || hasPendingSave()) { e.preventDefault(); e.returnValue = ""; } };
    window.addEventListener("beforeunload", warn);
    return () => { window.removeEventListener("beforeunload", warn); setUnsaved(false); };
  }, []);

  const setBundle = useCallback((b: DemoBundle) => {
    setBundleState(b);
    refreshNav();
  }, [refreshNav]);
  const setDirty = useCallback((d: boolean) => { dirty.current = d; setUnsaved(d); }, []);
  const go = useCallback((t: TabKey, query = "") => {
    if (dirty.current && !confirm("You have unsaved changes on this step. Leave without saving?")) return;
    setDirty(false);
    nav(`/admin/demos/${demoId}/${t}${query}`);
    window.scrollTo(0, 0);
  }, [demoId, nav, setDirty]);

  // The old Screens and Hotspots steps are one Studio now; keep their links working.
  if (tab === "screens" || tab === "hotspots") return <Navigate to={`/admin/demos/${demoId}/studio${search}`} replace />;
  if (loadError) return <div className="error-box">{loadError}</div>;
  if (!bundle) return <p className="muted">Loading…</p>;

  const body = (() => {
    switch (tab as TabKey) {
      case "basics": return <BasicsTab />;
      case "survey": return <SurveyTab />;
      case "outcomes": return <PathsTab />;
      case "studio": return <Studio />;
      case "preview": return <PreviewTab />;
      case "publish": return <PublishTab />;
      case "analytics": return <AnalyticsTab />;
      default: return <p>Unknown step.</p>;
    }
  })();
  const head = HEADINGS[tab];

  return (
    <Ctx.Provider value={{ bundle, setBundle, setDirty, go }}>
      {head && (
        <div className="page-head">
          <div>
            <h1>{head.title}</h1>
            <p className="sub">{head.sub}</p>
          </div>
        </div>
      )}
      {body}
    </Ctx.Provider>
  );
}

/** Footer used by the setup steps for guided forward motion. */
export function TabFooter({ back, next, nextLabel, children }: { back?: TabKey; next?: TabKey; nextLabel?: string; children?: React.ReactNode }) {
  const { go } = useEditor();
  const label = (k: TabKey) => TABS.find((t) => t.key === k)?.label ?? k;
  return (
    <div className="tab-foot">
      {back && <button className="btn btn-ghost" onClick={() => go(back)}>Back to {label(back)}</button>}
      <span className="spacer" />
      {children}
      {next && <button className="btn btn-primary" onClick={() => go(next)}>{nextLabel ?? `Continue to ${label(next)}`}</button>}
    </div>
  );
}
